import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock, Plus, Users, ShoppingBag, Mail, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import appApi, { getStoredUser, clearSession, hasPin, checkPin, clearPin, bioEnrolled, bioVerify, pushSubscribe, pushSubscribed, needsPinToday, markPinUnlockedToday } from "./appApi";
import { Splash, Greeting, PinPad, AuthBackdrop, ScreenIn, spring } from "./bits";
import { CodeLogin, PinSetup, DeactivatedScreen } from "./AuthScreens";
import InstallGate, { isStandaloneApp } from "./InstallGate";
import TodayScreen from "./TodayScreen";
import WizardScreen from "./WizardScreen";
import CustomersScreen from "./CustomersScreen";
import OrdersScreen from "./OrdersScreen";
import MessagesScreen from "./MessagesScreen";
import SettingsScreen from "./SettingsScreen";
import DevSuite from "./dev/DevSuite";

// PIN re-entry for a remembered session — 2 wrong attempts deactivate the account (owner reactivates).
// Biometrics unlock is offered only after the first successful PIN of the day.
const PinEntry = ({ user, onUnlock, onForgot, onLockdown }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const [tries, setTries] = useState(0);
  const bio = bioEnrolled() && !needsPinToday();

  useEffect(() => { if (bio) doBio(); /* eslint-disable-next-line */ }, []);

  const doBio = async () => { if (await bioVerify()) onUnlock(); };

  const change = async (v) => {
    setPin(v);
    if (v.length < 4) return;
    if (await checkPin(user.id, v)) {
      markPinUnlockedToday();
      onUnlock();
      return;
    }
    setShake(true);
    setPin("");
    const n = tries + 1;
    setTries(n);
    setTimeout(() => setShake(false), 450);
    if (n >= 2) {
      try { await appApi.post("/app/security/deactivate-self"); } catch { /* session may already be invalid */ }
      toast.error(m.pin_lockdown);
      onLockdown();
    } else {
      toast.error(m.pin_wrong);
    }
  };

  return (
    <AuthBackdrop>
      <div className="flex-1 flex flex-col items-center justify-center px-6 min-h-screen">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand/60">{m.welcome}</p>
        <h1 className="mt-1 font-heading text-4xl font-medium text-brand-ink">{user.name}</h1>
        <p className="mt-6 mb-4 text-sm text-brand-muted" data-testid="pin-entry-title">{m.pin_title}</p>
        <PinPad value={pin} onChange={change} maxLen={4} shake={shake} onBio={bio ? doBio : undefined} />
        <button onClick={onForgot} className="mt-8 text-sm font-bold text-brand-ink/50" data-testid="pin-forgot">{m.pin_forgot}</button>
      </div>
    </AuthBackdrop>
  );
};

// Animated bottom navigation — only tabs the user is allowed to see
const BottomNav = ({ tabs, active, onChange }) => (
  <nav className="fixed bottom-0 inset-x-0 z-40 px-3 pb-3" data-testid="app-bottom-nav">
    <div className="mx-auto max-w-md rounded-[1.75rem] bg-white/95 backdrop-blur border border-brand/10 shadow-[0_10px_40px_rgba(134,77,99,0.18)] flex items-stretch justify-around px-1 py-1.5 overflow-x-auto">
      {tabs.map((tb) => {
        const Icon = tb.icon;
        const on = active === tb.id;
        return (
          <motion.button
            key={tb.id}
            whileTap={{ scale: 0.85 }}
            onClick={() => onChange(tb.id)}
            className="relative flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-2xl flex-1 min-w-[46px] max-w-[72px]"
            data-testid={`app-tab-${tb.id}`}
          >
            {on && (
              <motion.span
                layoutId="nav-pill"
                transition={spring}
                className="absolute inset-0 rounded-2xl bg-brand-soft"
              />
            )}
            <motion.span animate={on ? { y: [0, -5, 0] } : {}} transition={{ duration: 0.35 }} className="relative">
              <Icon className={`h-5 w-5 max-[360px]:h-[18px] max-[360px]:w-[18px] ${on ? "text-brand" : "text-brand-ink/35"}`} />
            </motion.span>
            <span className={`relative text-[10px] max-[360px]:text-[8px] font-bold truncate max-w-full ${on ? "text-brand" : "text-brand-ink/35"}`}>{tb.label}</span>
          </motion.button>
        );
      })}
    </div>
  </nav>
);

const StaffAppInner = () => {
  const { t } = useLang();
  const m = t.mapp;
  const [params, setParams] = useSearchParams();
  const [stage, setStage] = useState("splash"); // splash → login | pin → (pinsetup) → greeting → main
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("today");

  // boot: splash → verify stored session
  useEffect(() => {
    const tmr = setTimeout(async () => {
      const stored = getStoredUser();
      if (!stored) { setStage("login"); return; }
      try {
        const res = await appApi.get("/app/me");
        setUser(res.data);
        localStorage.setItem("cfm_app_user", JSON.stringify(res.data));
        setStage(hasPin() ? "pin" : "greeting");
        if (!hasPin()) setTimeout(() => setStage("pinsetup"), 1500);
      } catch (err) {
        if (err.response?.status === 403) {
          setUser(getStoredUser());
          setStage("deactivated");
        } else {
          clearSession();
          setStage("login");
        }
      }
    }, 1900);
    return () => clearTimeout(tmr);
  }, []);

  // deep link ?tab=orders (push notification target)
  useEffect(() => {
    const target = params.get("tab");
    if (target && stage === "main") { setTab(target); params.delete("tab"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Immediate kick on deactivation / expired session while the app is in use
  useEffect(() => {
    const onDeactivated = () => setStage("deactivated");
    const onUnauthorized = () => { clearSession(); setUser(null); setStage("login"); };
    window.addEventListener("cfm-app-deactivated", onDeactivated);
    window.addEventListener("cfm-app-unauthorized", onUnauthorized);
    return () => {
      window.removeEventListener("cfm-app-deactivated", onDeactivated);
      window.removeEventListener("cfm-app-unauthorized", onUnauthorized);
    };
  }, []);

  // Notifications default ON: right after entering the app, users with the
  // permission are (re)subscribed for push on this device (browser may prompt once).
  useEffect(() => {
    if (stage === "main" && user?.perms?.notifications && !pushSubscribed() && "Notification" in window && Notification.permission === "default") {
      pushSubscribe().catch(() => { /* user can enable it later in Settings */ });
    }
  }, [stage, user]);

  const afterLogin = (u) => {
    setUser(u);
    setStage("greeting");
    setTimeout(() => setStage(hasPin() ? "main" : "pinsetup"), 1700);
    // best-effort push re-arm for users with the permission
    if (u.perms?.notifications && pushSubscribed()) pushSubscribe().catch(() => {});
  };

  const unlock = () => {
    setStage("greeting");
    setTimeout(() => setStage("main"), 1700);
    if (user?.perms?.notifications && pushSubscribed()) pushSubscribe().catch(() => {});
  };

  const forgotPin = () => { clearSession(); setUser(null); setStage("login"); };
  const logout = () => { clearSession(); setUser(null); setStage("login"); };

  const tabs = useMemo(() => {
    if (!user) return [];
    const p = user.perms || {};
    const list = [];
    if (p.schedule) list.push({ id: "today", label: m.tab_today, icon: CalendarClock });
    if (p.bookings) list.push({ id: "new", label: m.tab_new, icon: Plus });
    if (p.customers) list.push({ id: "customers", label: m.tab_customers, icon: Users });
    if (p.orders) list.push({ id: "orders", label: m.tab_orders, icon: ShoppingBag });
    if (p.messages) list.push({ id: "messages", label: m.tab_messages, icon: Mail });
    if (user.badge === "management") list.push({ id: "dev", label: m.tab_dev, icon: ShieldCheck });
    list.push({ id: "settings", label: m.tab_settings, icon: SettingsIcon });
    return list;
  }, [user, m]);

  const activeTab = tabs.some((tb) => tb.id === tab) ? tab : (tabs[0]?.id || "settings");

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink" data-testid="staff-app">
      <AnimatePresence mode="wait">
        {stage === "splash" && <Splash key="splash" />}
        {stage === "greeting" && user && <Greeting key="greeting" name={user.name} text={m.welcome} />}
      </AnimatePresence>

      {stage === "login" && <CodeLogin onLoggedIn={afterLogin} onDeactivated={() => setStage("deactivated")} />}
      {stage === "pin" && user && <PinEntry user={user} onUnlock={unlock} onForgot={forgotPin} onLockdown={() => setStage("deactivated")} />}
      {stage === "pinsetup" && user && <PinSetup user={user} onDone={() => setStage("main")} />}
      {stage === "deactivated" && <DeactivatedScreen onBack={() => { clearSession(); setUser(null); setStage("login"); }} />}

      {stage === "main" && user && (
        <div className="mx-auto max-w-md px-5 pt-8 pb-4 min-h-screen">
          <AnimatePresence mode="wait">
            <ScreenIn k={activeTab}>
              {activeTab === "today" && <TodayScreen user={user} />}
              {activeTab === "new" && <WizardScreen onDone={() => setTab("today")} />}
              {activeTab === "customers" && <CustomersScreen />}
              {activeTab === "orders" && <OrdersScreen />}
              {activeTab === "messages" && <MessagesScreen />}
              {activeTab === "dev" && user.badge === "management" && <DevSuite />}
              {activeTab === "settings" && <SettingsScreen user={user} onLogout={logout} />}
            </ScreenIn>
          </AnimatePresence>
          <BottomNav tabs={tabs} active={activeTab} onChange={setTab} />
        </div>
      )}
    </div>
  );
};

// Mandatory PWA install: opened in a browser tab → install instructions only.
// The real app unlocks only when launched from the home-screen icon (standalone).
const StaffApp = () => (isStandaloneApp() ? <StaffAppInner /> : <InstallGate />);

export default StaffApp;
